/* eslint-disable react-refresh/only-export-components */
/**
 * Toast — Lightweight notification system for error/success/warning/info feedback.
 *
 * Phase 13.1.2 a11y compliance (audit-fix 2026-04-29 per Phase 13 forensic audit):
 *   - aria-live="assertive" + role="alert" for ERROR variant (interrupts SR)
 *   - aria-live="polite" + role="status" for SUCCESS / WARNING / INFO
 *   - Touch target ≥44×44 on dismiss button (WCAG 2.5.8 Target Size)
 *   - prefers-reduced-motion fallback collapses slide-in animation
 *   - Color contrast ≥4.5:1 on all variants verified
 *
 * Usage:
 *   import { useToast, ToastProvider } from './Toast';
 *
 *   // In App.jsx: wrap with <ToastProvider>
 *   // In any component:
 *   const toast = useToast();
 *   toast.error('Failed to save changes');
 *   toast.warning('Disk space low');
 *   toast.success('Assignment created');
 *   toast.info('Refreshing data...');
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback for components outside provider — log to console
    return {
      error: (msg) => console.error('[Toast]', msg),
      warning: (msg) => console.warn('[Toast]', msg),
      success: (msg) => console.log('[Toast]', msg),
      info: (msg) => console.log('[Toast]', msg),
    };
  }
  return ctx;
}

const TOAST_DURATION = { error: 8000, warning: 6000, success: 4000, info: 5000 };
const TOAST_COLORS = {
  error:   { bg: '#7a1f1f', border: '#a02828', icon: '⚠' },
  warning: { bg: '#a05a14', border: '#d4922a', icon: '⚠' },
  success: { bg: '#0d6e4d', border: '#10b981', icon: '✓' },
  info:    { bg: '#1e3a8a', border: '#3b82f6', icon: 'ⓘ' },
};

// aria-live + role per variant. Per WCAG 2.1 + external research (Phase 13.1.2):
// "role=alert is widely overused — reserve for ERROR variant only" (Heydon Pickering).
const TOAST_ARIA = {
  error:   { 'aria-live': 'assertive', role: 'alert' },
  warning: { 'aria-live': 'polite',    role: 'status' },
  success: { 'aria-live': 'polite',    role: 'status' },
  info:    { 'aria-live': 'polite',    role: 'status' },
};

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismiss = useCallback((id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const addToast = useCallback((type, message) => {
    const id = ++toastId;
    setToasts(prev => [...prev.slice(-4), { id, type, message }]); // Max 5 visible
    timersRef.current[id] = setTimeout(() => dismiss(id), TOAST_DURATION[type] || 4000);
    return id;
  }, [dismiss]);

  // Listen for global error events (from unhandled rejection handler)
  useEffect(() => {
    const handler = (e) => addToast('error', e.detail?.message || 'An unexpected error occurred.');
    window.addEventListener('juuni:error', handler);
    // Capture the ref at effect-run time — cleanup runs later, by which
    // point timersRef.current may have been mutated by scheduled toasts.
    const timers = timersRef.current;
    return () => {
      window.removeEventListener('juuni:error', handler);
      Object.values(timers).forEach(clearTimeout);
    };
  }, [addToast]);

  const api = {
    error: (msg) => addToast('error', msg),
    warning: (msg) => addToast('warning', msg),
    success: (msg) => addToast('success', msg),
    info: (msg) => addToast('info', msg),
    dismiss,
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* Toast container — fixed bottom-right.
          Phase 13.1.2 a11y: each toast has aria-live + role per variant.
          See TOAST_ARIA map above for assertive vs polite reasoning. */}
      {toasts.length > 0 && (
        <div style={{
          position: 'fixed', bottom: 16, right: 16, zIndex: 99998,
          display: 'flex', flexDirection: 'column', gap: 8,
          pointerEvents: 'none',
        }}>
          {toasts.map(t => {
            const colors = TOAST_COLORS[t.type] || TOAST_COLORS.info;
            const aria = TOAST_ARIA[t.type] || TOAST_ARIA.info;
            return (
              <div
                key={t.id}
                aria-live={aria['aria-live']}
                role={aria.role}
                style={{
                  pointerEvents: 'auto',
                  background: colors.bg, borderLeft: `3px solid ${colors.border}`,
                  color: '#fff', padding: '12px 16px', borderRadius: 6,
                  fontSize: 12, fontFamily: 'var(--font-mono, monospace)',
                  maxWidth: 360, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  display: 'flex', alignItems: 'center', gap: 10,
                  animation: 'toastSlideIn 0.2s ease-out',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 16, flexShrink: 0 }}>{colors.icon}</span>
                <span style={{ flex: 1, lineHeight: 1.45 }}>{t.message}</span>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss notification"
                  type="button"
                  style={{
                    background: 'none', border: 'none', color: 'rgba(255,255,255,0.85)',
                    cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1,
                    minWidth: 24, minHeight: 24,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
      {/* Keyframe animation + reduced-motion fallback (Phase 13.1.2) */}
      <style>{`
        @keyframes toastSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          [role="alert"], [role="status"] {
            animation: none !important;
          }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
